/**
 * OAuth Callback Handler
 * Processes OAuth redirects and exchanges session for tokens
 */

import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '../store/auth';
import { Sparkles, Loader2, AlertCircle } from 'lucide-react';

export default function AuthCallback() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { setAuth } = useAuthStore();
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const handleCallback = async () => {
            const sessionId = searchParams.get('session');
            const redirect = searchParams.get('redirect') || '/dashboard';
            const errorParam = searchParams.get('error');

            if (errorParam) {
                setError(getErrorMessage(errorParam));
                return;
            }

            if (!sessionId) {
                setError('Invalid callback - no session');
                return;
            }

            try {
                // Exchange session for tokens
                const apiUrl = import.meta.env.VITE_API_URL || 'https://ai-operations-assistant.anikchowdhury.workers.dev/api/v1';
                const response = await fetch(`${apiUrl}/auth/google/exchange`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ sessionId })
                });

                const data = await response.json();

                if (!response.ok || !data.success) {
                    setError(data.error || 'Authentication failed');
                    return;
                }

                // Set auth state
                setAuth({
                    accessToken: data.accessToken,
                    refreshToken: data.refreshToken,
                    user: data.user,
                    expiresIn: data.expiresIn || 3600,
                    isNewUser: data.isNewUser
                });

                // Redirect - ProtectedRoute will handle onboarding redirect for new users
                navigate('/dashboard', { replace: true });

            } catch (err) {
                console.error('OAuth callback error:', err);
                setError('Failed to complete authentication');
            }
        };

        handleCallback();
    }, [searchParams, navigate, setAuth]);

    const getErrorMessage = (code: string): string => {
        switch (code) {
            case 'oauth_denied':
                return 'You cancelled the sign-in process';
            case 'invalid_request':
                return 'Invalid authentication request';
            case 'invalid_state':
                return 'Security check failed. Please try again';
            case 'token_failed':
                return 'Failed to authenticate with Google';
            case 'no_email':
                return 'Could not get email from Google account';
            case 'user_creation_failed':
                return 'Failed to create account';
            case 'oauth_failed':
                return 'Google authentication failed';
            default:
                return 'Authentication error occurred';
        }
    };

    if (error) {
        return (
            <div className="min-h-screen bg-dark-950 flex items-center justify-center p-4">
                <div className="max-w-md w-full text-center">
                    <div className="w-16 h-16 bg-red-500/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
                        <AlertCircle className="w-8 h-8 text-red-400" />
                    </div>
                    <h1 className="text-2xl font-bold mb-2">Authentication Failed</h1>
                    <p className="text-dark-400 mb-6">{error}</p>
                    <button
                        onClick={() => navigate('/login')}
                        className="btn btn-primary"
                    >
                        Back to Login
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-dark-950 flex items-center justify-center p-4">
            <div className="max-w-md w-full text-center">
                <div className="w-16 h-16 bg-primary-600 rounded-2xl flex items-center justify-center mx-auto mb-6">
                    <Sparkles className="w-8 h-8 text-white" />
                </div>
                <h1 className="text-2xl font-bold mb-2">Signing you in...</h1>
                <p className="text-dark-400 mb-6">Please wait while we complete authentication</p>
                <Loader2 className="w-8 h-8 animate-spin mx-auto text-primary-400" />
            </div>
        </div>
    );
}
